import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RoleBadge from "@/components/RoleBadge";
import StaffAvatar from "@/components/StaffAvatar";
import { ComplianceBadge } from "@/pages/StaffList";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const TYPE_LABELS = {
  enhanced_dbs: "Enhanced DBS",
  safeguarding_training: "Safeguarding Training",
  first_aid: "First Aid",
  send_training: "SEND Training",
  itt_competency: "Independent Travel Training Competency",
  other: "Other",
};

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", preferred_name: "", emergency_contact_name: "", emergency_contact_phone: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [photoId, setPhotoId] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const photoInputRef = useRef(null);

  const load = useCallback(() => {
    api.get("/staff/me").then((r) => {
      setForm({
        name: r.data.name || "",
        phone: r.data.profile?.phone || "",
        preferred_name: r.data.profile?.preferred_name || "",
        emergency_contact_name: r.data.profile?.emergency_contact_name || "",
        emergency_contact_phone: r.data.profile?.emergency_contact_phone || "",
      });
      setPhotoId(r.data.profile?.photo_document_id || null);
      setLoaded(true);
    }).catch(() => setLoaded(true));
    api.get(`/compliance/staff/${user.id}`).then((r) => setCompliance(r.data)).catch(() => {});
  }, [user.id]);

  useEffect(() => {
    load();
  }, [load]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/staff/me", form);
      setUser((u) => ({ ...u, name: data.name }));
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "staff_photo");
    fd.append("staff_id", user.id);
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photograph updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 10) {
      toast.error("New password must be at least 10 characters");
      return;
    }
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password changed");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div data-testid="profile-page" className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-4">
        <div className="relative">
          <StaffAvatar photoId={photoId} name={user?.name} size="h-16 w-16" testid="profile-photo" />
          <button
            data-testid="profile-photo-upload-button"
            onClick={() => photoInputRef.current?.click()}
            className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-white shadow hover:bg-brand-hover transition-colors duration-200"
            aria-label="Upload photograph"
          >
            <Camera className="h-3.5 w-3.5" />
          </button>
          <input ref={photoInputRef} type="file" accept="image/*" className="hidden" data-testid="profile-photo-input"
            onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">My profile</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <RoleBadge role={user?.role} testid="profile-role-badge" />
            <Badge variant="secondary" className="font-mono text-xs">{user?.staff_number}</Badge>
            <span className="text-sm text-slate-600">{user?.email}</span>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Personal details</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={saveProfile} className="space-y-4" data-testid="profile-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Display name</Label>
                <Input data-testid="profile-name-input" value={form.name} disabled={!loaded} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Preferred name</Label>
                <Input data-testid="profile-preferredname-input" value={form.preferred_name} disabled={!loaded} onChange={(e) => setForm({ ...form, preferred_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Telephone</Label>
                <Input data-testid="profile-phone-input" value={form.phone} disabled={!loaded} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Emergency contact name</Label>
                <Input data-testid="profile-emergency-name-input" value={form.emergency_contact_name} disabled={!loaded} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Emergency contact phone</Label>
                <Input data-testid="profile-emergency-phone-input" value={form.emergency_contact_phone} disabled={!loaded} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} /></div>
            </div>
            <p className="text-xs text-slate-500">Your role and permissions are managed by an administrator and cannot be changed here.</p>
            <Button data-testid="profile-save-button" type="submit" disabled={saving || !loaded}
              className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">My compliance</CardTitle></CardHeader>
        <CardContent>
          {!compliance ? (
            <p className="text-sm text-slate-600" data-testid="profile-compliance-loading">Loading…</p>
          ) : compliance.items.length === 0 ? (
            <p className="text-sm text-slate-600" data-testid="profile-compliance-empty">No compliance records yet. Your manager maintains these records.</p>
          ) : (
            <div className="space-y-2" data-testid="profile-compliance-list">
              {compliance.items.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2.5" data-testid={`profile-compliance-${r.id}`}>
                  <span className="text-sm font-medium text-slate-800 flex-1">{TYPE_LABELS[r.type] || r.type}{r.label ? ` — ${r.label}` : ""}</span>
                  <ComplianceBadge status={r.status} />
                  {r.verified && <Badge className="bg-ukgreen-light text-ukgreen border-transparent">Verified</Badge>}
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-500 mt-3">Compliance records are verified by authorised managers — you cannot verify your own records.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Change password</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={changePassword} className="space-y-4" data-testid="change-password-form">
            <div className="space-y-1.5"><Label>Current password</Label>
              <Input data-testid="change-current-password-input" type="password" autoComplete="current-password" required
                value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>New password</Label>
                <Input data-testid="change-new-password-input" type="password" autoComplete="new-password" required
                  value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Confirm new password</Label>
                <Input data-testid="change-confirm-password-input" type="password" autoComplete="new-password" required
                  value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} /></div>
            </div>
            <Button data-testid="change-password-submit-button" type="submit" disabled={changingPw}
              className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
              {changingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Change password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Your access</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2" data-testid="profile-permissions-list">
            {(user?.permissions || []).map((p) => (
              <Badge key={p} variant="secondary" className="font-mono text-xs">{p}</Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
